import { useMemo, useState } from 'react';
import { Users, Star, FolderKanban, Plus, RefreshCw, Loader2, MoreHorizontal, Edit3, Copy, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { useCopy } from '../../i18n';
import { useProject } from '../../contexts/ProjectContext';
import { useUIState } from '../../contexts/UIStateContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useProjectFolders } from '../../contexts/ProjectFolderContext';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import ConfirmModal from '../common/ConfirmModal';
import EmptyState from '../common/EmptyState';
import type { ProjectWithRole } from '../../types/database';
import MobileScreen from './MobileScreen';
import EquipeScreen from './EquipeScreen';

/**
 * A lista de projetos do celular. Uma linha por projeto, e só uma.
 *
 * A gaveta do desktop mostra o MESMO projeto até três vezes: uma em Favoritos,
 * uma dentro da pasta e uma na organização. Num monitor a repetição é atalho;
 * em 375px é rolagem cega passando pela mesma coisa, e ainda destaca duas
 * cópias ao mesmo tempo, sugerindo que há dois projetos abertos.
 *
 * Aqui favorito, pasta e organização deixam de ser SEÇÕES e viram ATRIBUTOS da
 * linha: a estrela à esquerda e a procedência no subtítulo. A informação toda
 * continua na tela, ocupando um terço do espaço.
 *
 * Tocar abre o projeto direto no Quadro. Não há passo intermediário de escolher
 * módulo: no celular o Kanban é a tela de execução, e perguntar "qual módulo?"
 * depois de "qual projeto?" é a pergunta a mais que este redesenho existe para
 * eliminar.
 */
interface ProjetosScreenProps {
  /** Sair da tela do shell. Sem isto, trocar de módulo deixaria esta lista
   *  renderizada por cima: o toque "não faria nada" aos olhos do usuário. */
  onSair: () => void;
}

const ProjetosScreen = ({ onSair }: ProjetosScreenProps) => {
  const t = useCopy().common.projetosScreen;
  const {
    projects, currentProjectId, switchProject, loading,
    createProject, loadProjects,
    toggleProjectFavorite, renameProject, duplicateProject, deleteProject,
  } = useProject();
  // `menuDe` guarda o PROJETO e nao o id: o titulo da folha e o nome, e reler a
  // lista a cada render para achar o id de volta seria trabalho por nada.
  const [menuDe, setMenuDe] = useState<ProjectWithRole | null>(null);
  const [renomeando, setRenomeando] = useState(false);
  const [novoNome, setNovoNome] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  /** Sub-tela de equipe. Guarda o PROJETO, como `menuDe`. */
  const [equipeDe, setEquipeDe] = useState<ProjectWithRole | null>(null);
  const [criando, setCriando] = useState(false);
  const [nome, setNome] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [atualizando, setAtualizando] = useState(false);
  const { changeModule } = useUIState();
  const { organizations } = useOrganization();
  const { folders } = useProjectFolders();

  const linhas = useMemo(() => {
    const nomeOrg = new Map(organizations.map(o => [o.id, o.name]));
    const nomePasta = new Map(folders.map(f => [f.id, f.name]));

    return projects.map(p => {
      // Procedência em uma frase, na ordem do mais específico para o mais amplo.
      const partes: string[] = [];
      const pasta = p.folder_id ? nomePasta.get(p.folder_id) : null;
      const org = p.organization_id ? nomeOrg.get(p.organization_id) : null;
      if (pasta) partes.push(pasta);
      if (org) partes.push(org);
      return { projeto: p, procedencia: partes.join(' · ') || t.pessoal };
    });
  }, [projects, organizations, folders, t.pessoal]);

  /**
   * Criar projeto no celular existe, e so o BASICO existe: nome e pronto.
   *
   * O caminho do desktop passa por galeria de modelos e geracao por IA, que sao
   * decisoes de planejamento e pedem tela grande. Aqui a pergunta e outra:
   * "surgiu um projeto agora, quero registrar antes de esquecer". Bloquear isso
   * obrigava a pessoa a abrir o computador para digitar um nome.
   */
  const criar = async () => {
    const limpo = nome.trim();
    if (!limpo) return;
    setSalvando(true);
    try {
      const novo = await createProject(limpo);
      setCriando(false);
      setNome('');
      if (novo) { switchProject(novo.id); changeModule('kanban'); onSair(); }
    } catch (err) {
      // PLAN_LIMIT_REACHED e o caso comum: o Free tem teto de projetos.
      const limite = (err as Error)?.message === 'PLAN_LIMIT_REACHED';
      toast.error(limite ? t.limiteAtingido : t.erroCriar);
    } finally {
      setSalvando(false);
    }
  };

  /**
   * Atualizar a mao, porque o celular fica horas em segundo plano.
   *
   * O app carrega projeto no arranque e nao volta a buscar. No desktop a aba
   * fica aberta e a pessoa recarrega sem pensar; no telefone ela volta ao app
   * depois de meio dia e ve a lista de antes, sem nada na tela dizendo que
   * aquilo e uma foto velha.
   */
  const atualizar = async () => {
    setAtualizando(true);
    try { await loadProjects(); }
    finally { setAtualizando(false); }
  };

  const fecharMenu = () => {
    setMenuDe(null);
    setRenomeando(false);
    setConfirmandoExclusao(false);
  };

  const renomear = async () => {
    const limpo = novoNome.trim();
    if (!menuDe || !limpo || limpo === menuDe.name) { setRenomeando(false); return; }
    setOcupado(true);
    try { await renameProject(menuDe.id, limpo); fecharMenu(); }
    catch { toast.error(t.erroRenomear); }
    finally { setOcupado(false); }
  };

  /**
   * Duplicar NAO abre a copia, diferente de criar.
   *
   * Quem cria quer comecar a trabalhar no que criou. Quem duplica quer o
   * original preservado e a copia na lista, para renomear ou ajustar depois:
   * ser levado embora da lista seria o oposto da intencao.
   */
  const duplicar = async () => {
    if (!menuDe) return;
    setOcupado(true);
    try {
      await duplicateProject(menuDe.id);
      fecharMenu();
      toast.success(t.duplicado);
    } catch { toast.error(t.erroDuplicar); }
    finally { setOcupado(false); }
  };

  const excluir = async () => {
    if (!menuDe) return;
    setOcupado(true);
    try { await deleteProject(menuDe.id); fecharMenu(); toast.success(t.excluido); }
    catch { toast.error(t.erroExcluir); }
    finally { setOcupado(false); }
  };

  /** Item da folha de acoes. Alvo de 48px, como toda linha desta tela. */
  const acao = (
    chave: string,
    Icone: typeof Star,
    rotulo: string,
    aoTocar: () => void,
    perigo = false,
  ) => (
    <button
      key={chave}
      data-testid={`projeto-acao-${chave}`}
      onClick={aoTocar}
      className={cn(
        'flex items-center gap-3 px-5 py-3.5 text-left hover:bg-[var(--color-bg-secondary)]',
        perigo && 'text-[var(--color-danger)]'
      )}
    >
      <Icone size={18} className={cn('shrink-0', !perigo && 'text-[var(--color-text-muted)]')} />
      <span className={cn('text-[15px]', !perigo && 'text-[var(--color-text)]')}>{rotulo}</span>
    </button>
  );

  const abrir = (id: string) => {
    switchProject(id);
    changeModule('kanban');
    onSair();
  };

  /**
   * A equipe substitui a lista enquanto estiver aberta.
   *
   * Sub-tela e não folha de baixo: a folha serve para uma decisão curta (as
   * quatro ações do projeto cabem nela), e aqui há uma lista de pessoas que
   * rola, mais um formulário de convite. Empilhar isso numa folha de 92dvh
   * criaria rolagem dentro de rolagem.
   *
   * Vem ANTES da guarda de carregamento: a tela de equipe busca os próprios
   * dados e não depende da lista de projetos estar pronta.
   */
  if (equipeDe) {
    return <EquipeScreen projeto={equipeDe} aoVoltar={() => setEquipeDe(null)} />;
  }

  if (loading && projects.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-[var(--color-text-muted)]">
        <div className="w-6 h-6 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (projects.length === 0) {
    return <EmptyState icon={FolderKanban} title={t.vazioTitulo} description={t.vazioDescricao} />;
  }

  return (
    <MobileScreen
      titulo={t.titulo}
      apoio={projects.length === 1 ? t.contagemUm : t.contagem.replace('{{n}}', String(projects.length))}
    >
      <div className="flex items-center gap-2 mb-3">
        {criando ? (
          <>
            <Input
              autoFocus
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') criar(); if (e.key === 'Escape') setCriando(false); }}
              placeholder={t.nomePlaceholder}
              className="h-11 flex-1 text-[15px]"
            />
            <Button variant="primary" size="lg" className="h-11 shrink-0" disabled={!nome.trim() || salvando} onClick={criar}>
              {salvando ? <Loader2 size={15} className="animate-spin" /> : t.criar}
            </Button>
          </>
        ) : (
          <>
            <Button variant="primary" size="lg" className="h-11 flex-1" onClick={() => setCriando(true)}>
              <Plus size={16} /> {t.novoProjeto}
            </Button>
            <Button
              variant="outline"
              size="lg"
              /* h-11 = 44px, o piso de alvo de toque. `size="sm"` da ~26px e
                 ainda traz a classe de 12px, que nao existe na escala. O resto
                 desta tela usa py-3.5 (~48px), entao o botao pequeno destoava do
                 proprio padrao da tela alem de ser dificil de acertar. */
              className="h-11 w-11 shrink-0 p-0"
              aria-label={t.atualizar}
              data-testid="projetos-atualizar"
              onClick={atualizar}
            >
              <RefreshCw size={15} className={atualizando ? 'animate-spin' : undefined} />
            </Button>
          </>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {linhas.map(({ projeto, procedencia }) => (
          // A linha deixou de ser <button> e virou <div role="button">: ela
          // passou a conter dois controles proprios (estrela e "..."), e botao
          // dentro de botao e DOM invalido, alem de fazer o clique da casca
          // engolir o do filho.
          <div
            key={projeto.id}
            role="button"
            tabIndex={0}
            data-testid="projeto-row"
            data-project-id={projeto.id}
            onClick={() => abrir(projeto.id)}
            onKeyDown={(e) => { if (e.key === 'Enter') abrir(projeto.id); }}
            className={cn(
              'flex items-center gap-1 pl-1 pr-1 py-1 text-left cursor-pointer',
              'bg-[var(--color-bg)] border border-[var(--color-border)] rounded-[var(--radius-md)]',
              // O projeto corrente se marca pela borda, e não por fundo cheio:
              // fundo preenchido numa lista inteira de cartões vira mancha.
              projeto.id === currentProjectId && 'border-[var(--color-primary)]'
            )}
          >
            {/* A estrela era DECORATIVA: mostrava o estado e nao respondia ao
                toque. Parecer clicavel sem ser e pior do que nao existir. Agora
                e alvo proprio de 44px, e favoritar (a acao mais frequente) sai
                num toque, sem passar pelo menu. */}
            <button
              data-testid="projeto-favorito"
              aria-label={projeto.isFavorited ? t.desfavoritar : t.favoritar}
              aria-pressed={projeto.isFavorited}
              onClick={(e) => { e.stopPropagation(); void toggleProjectFavorite(projeto.id); }}
              className="shrink-0 w-11 h-11 grid place-items-center rounded-[var(--radius-md)]"
            >
              <Star
                size={16}
                className={cn(
                  projeto.isFavorited
                    ? 'text-[var(--color-warning)] fill-[var(--color-warning)]'
                    : 'text-[var(--color-text-muted)] opacity-40'
                )}
              />
            </button>

            <div className="flex-1 min-w-0 py-1.5">
              <div className="text-[15px] font-medium text-[var(--color-text)] truncate">
                {projeto.name}
              </div>
              <div className="text-[11px] text-[var(--color-text-secondary)] truncate mt-0.5">
                {procedencia}
              </div>
            </div>

            {/* "..." no lugar do chevron: a seta promete "abre", e a linha
                inteira ja faz isso. Duas promessas do mesmo gesto na mesma
                linha e o que fazia o chevron nao valer o espaco que ocupava. */}
            <button
              data-testid="projeto-menu"
              aria-label={t.acoes}
              onClick={(e) => { e.stopPropagation(); setMenuDe(projeto); }}
              className="shrink-0 w-11 h-11 grid place-items-center rounded-[var(--radius-md)] text-[var(--color-text-muted)]"
            >
              <MoreHorizontal size={18} />
            </button>
          </div>
        ))}
      </div>

      {/* Folha de acoes. Abaixo de md o DialogContent ja e ancorado embaixo, que
          e onde o polegar alcanca; mesmo padrao do "Mover para" do HojeModule,
          entao nao nasce primitivo novo. */}
      <Dialog open={!!menuDe} onOpenChange={(aberto) => { if (!aberto) fecharMenu(); }}>
        <DialogContent className="p-0 gap-0 bg-[var(--color-bg)]">
          <DialogTitle className="px-5 pt-5 pb-3 text-[15px] truncate">
            {menuDe?.name}
          </DialogTitle>

          {renomeando ? (
            <div className="flex items-center gap-2 px-5 pb-5">
              <Input
                autoFocus
                value={novoNome}
                onChange={(e) => setNovoNome(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') renomear(); if (e.key === 'Escape') setRenomeando(false); }}
                className="h-11 flex-1 text-[15px]"
              />
              <Button
                variant="primary"
                size="lg"
                className="h-11 shrink-0"
                disabled={!novoNome.trim() || ocupado}
                onClick={renomear}
              >
                {ocupado ? <Loader2 size={15} className="animate-spin" /> : t.salvar}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col pb-2">
              {acao('favorito', Star, menuDe?.isFavorited ? t.desfavoritar : t.favoritar,
                () => { if (menuDe) { void toggleProjectFavorite(menuDe.id); fecharMenu(); } })}

              {/* Renomear e Excluir so para o dono, igual ao desktop. ESCONDER e
                  nao desabilitar: item cinza que nao responde nao ensina nada. */}
              {menuDe?.userRole === 'owner' && acao('renomear', Edit3, t.renomear, () => {
                setNovoNome(menuDe.name);
                setRenomeando(true);
              })}

              {acao('duplicar', Copy, t.duplicar, duplicar)}

              {/* Equipe entra para TODO papel: ver quem está no projeto não é
                  privilégio, e quem não pode convidar nem remover simplesmente
                  não vê esses controles lá dentro.

                  Mora aqui, e não numa aba da barra inferior, porque a
                  capacidade é POR PROJETO: a folha de ações já guarda o projeto
                  e o papel do usuário nele. É a mesma régua que trouxe
                  Favoritar, Renomear, Duplicar e Excluir para cá. */}
              {acao('equipe', Users, t.equipe, () => {
                if (menuDe) { setEquipeDe(menuDe); fecharMenu(); }
              })}

              {menuDe?.userRole === 'owner' && acao('excluir', Trash2, t.excluir,
                () => setConfirmandoExclusao(true), true)}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Unica acao desta tela sem desfazer, e por isso a unica que confirma
          antes. Concluir tarefa e favoritar sao otimistas justamente porque tem
          volta. */}
      {confirmandoExclusao && menuDe && (
        <ConfirmModal
          isOpen
          title={t.excluirTitulo}
          message={t.excluirMensagem.replace('{{nome}}', menuDe.name)}
          confirmLabel={t.excluir}
          variant="danger"
          isLoading={ocupado}
          onConfirm={excluir}
          /* Cancelar fecha TUDO, e nao so a confirmacao.
           *
           * Fechando so a confirmacao, a folha de acoes voltava por cima da
           * barra inferior: quem desistia da exclusao ficava preso num menu e
           * precisava de um toque extra so para sair dele, com o "Excluir" de
           * novo a um dedo de distancia. Dizer "nao" a uma acao sem desfazer
           * deve devolver a pessoa a lista, nao ao menu que a ofereceu. */
          onCancel={fecharMenu}
        />
      )}
    </MobileScreen>
  );
};

export default ProjetosScreen;
